import { useCallback, useRef } from "react";

type UseElementVisibleOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number;
};

export default function useElementVisible() {
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  const observeElement = useCallback(
    (id: string, onVisible: () => void, options?: UseElementVisibleOptions) => {
      const checkAndObserve = () => {
        const element = document.getElementById(id);
        if (!element) return;

        const observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                onVisible();
                obs.disconnect();
              }
            });
          },
          {
            root: options?.root || null,
            rootMargin: options?.rootMargin || "0px",
            threshold: options?.threshold ?? 0.1,
          }
        );

        observer.observe(element);
      };

      if (document.getElementById(id)) {
        checkAndObserve();
        return;
      }

      mutationObserverRef.current?.disconnect();
      const observer = new MutationObserver(() => {
        if (document.getElementById(id)) {
          mutationObserverRef.current?.disconnect();
          checkAndObserve();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      mutationObserverRef.current = observer;
    },
    []
  );

  return { observeElement };
}
